import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Camera, Confetti, QrCode } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import QrScanner from "../components/QrScanner";

const ENTRY_POINTS = ["MAIN ENTRANCE", "VIP", "SIDE DOOR", "RE-ENTRY"];

export default function StaffScanner() {
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [denied, setDenied] = useState(false);
  const [doorCount, setDoorCount] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const prevCountRef = useRef(null);
  const milestonesRef = useRef([]);

  const CONFETTI = useRef(
    Array.from({ length: 36 }, (_, i) => ({
      left: (i * 37) % 100,
      delay: (i % 12) * 0.12,
      size: 6 + (i % 3) * 4,
      color: ["#F5B301", "#A855F7", "#ffffff", "#F9D976"][i % 4],
    }))
  ).current;

  const handleCount = (count, milestones) => {
    milestonesRef.current = milestones || [];
    const prev = prevCountRef.current;
    prevCountRef.current = count;
    setDoorCount(count);
    if (prev !== null && count > prev) {
      const crossed = milestonesRef.current.filter((m) => prev < m && m <= count);
      if (crossed.length) {
        setCelebration(Math.max(...crossed));
        setTimeout(() => setCelebration(null), 4500);
      }
    }
  };
  const [token, setToken] = useState("");
  const [entryPoint, setEntryPoint] = useState("MAIN ENTRANCE");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const tokenRef = useRef(null);

  useEffect(() => {
    API.get("/staff/venue").then((r) => setVenue(r.data)).catch(() => setDenied(true));
  }, []);

  useEffect(() => {
    if (denied) return;
    const fetchCount = () => API.get("/staff/door-count")
      .then((r) => handleCount(r.data.checkins, r.data.milestones)).catch(() => {});
    fetchCount();
    const timer = setInterval(fetchCount, 10000);
    return () => clearInterval(timer);
  }, [denied]);

  const doScan = async (tokenOverride = null) => {
    const code = String(tokenOverride || token).trim();
    if (!code) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await API.post("/business/scan", {
        token: code, entry_point: entryPoint, amount_paid: Number(amount) || 0,
      });
      setResult(data);
      setToken("");
      setAmount("");
      setShowCamera(false);
      API.get("/staff/door-count").then((r) => handleCount(r.data.checkins, r.data.milestones)).catch(() => {});
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
      setTimeout(() => tokenRef.current?.focus(), 100);
    }
  };

  return (
    <div data-testid="staff-scanner-page" className="mx-auto min-h-screen max-w-[430px] bg-[#050505] px-5 pb-12 text-white"
      style={{ paddingTop: "calc(1.5rem + env(safe-area-inset-top))" }}>
      <div className="flex items-center gap-3">
        <button data-testid="staff-back-btn" onClick={() => navigate(-1)} className="glass rounded-full p-2.5">
          <ArrowLeft size={16} />
        </button>
        <img src="/logo.png" alt="NITEPASS" className="h-9 w-auto" />
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-neon-gold">Door Staff</div>
      </div>

      {denied ? (
        <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center" data-testid="staff-denied">
          <p className="font-display text-lg font-black">No venue linked</p>
          <p className="mt-2 text-sm text-zinc-500">Ask a venue owner to add your email from their dashboard Staff tab.</p>
        </div>
      ) : (
        <>
          <div className="mt-5 rounded-2xl border border-neon-violet/30 bg-neon-violet/5 px-4 py-3 text-center" data-testid="staff-venue-chip">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Scanning for</div>
            <div className="font-display text-lg font-black">{venue?.name || "…"}</div>
          </div>

          {doorCount !== null && (
            <div className="mt-3 flex items-center justify-center gap-2 rounded-2xl border border-neon-gold/30 bg-neon-gold/5 px-4 py-2.5" data-testid="staff-door-count">
              <span className="h-2 w-2 animate-pulse rounded-full bg-neon-gold" />
              <span className="text-xs font-bold text-zinc-400">Tonight through the door:</span>
              <span className="font-display text-lg font-black text-neon-gold" data-testid="staff-door-count-value">{doorCount}</span>
            </div>
          )}

          <AnimatePresence>
            {celebration !== null && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/80"
                data-testid="door-milestone-flash">
                {CONFETTI.map((p, i) => (
                  <motion.span key={i}
                    className="absolute top-0 rounded-sm"
                    style={{ left: `${p.left}%`, width: p.size, height: p.size * 0.6, backgroundColor: p.color }}
                    initial={{ y: "-5vh", opacity: 1, rotate: 0 }}
                    animate={{ y: "105vh", opacity: [1, 1, 0.6, 0], rotate: 360 + (i % 5) * 120 }}
                    transition={{ duration: 2.4, delay: p.delay, ease: "easeIn" }} />
                ))}
                <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 16 }} className="relative text-center">
                  <Confetti size={52} weight="fill" className="mx-auto text-neon-gold" />
                  <div className="mt-3 font-display text-5xl font-black text-neon-gold" data-testid="door-milestone-text">
                    {celebration} GUESTS
                  </div>
                  <div className="mt-2 text-sm font-bold uppercase tracking-widest text-zinc-300">tonight — keep the line moving</div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {result && (
              <motion.div key={result.guest_name + result.total_points}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                className={`mt-5 rounded-3xl border p-6 text-center ${result.already_checked_in ? "border-neon-yellow/40 bg-neon-yellow/5" : "border-neon-green/40 bg-neon-green/5"}`}
                data-testid="staff-scan-result">
                <div className="font-display text-2xl font-black">
                  {result.already_checked_in ? "ALREADY IN" : `WELCOME, ${result.guest_name?.toUpperCase()}`}
                </div>
                <div className="mt-1 text-xs font-bold text-zinc-400">{result.guest_handle}</div>
                {result.reward_unlocked && (
                  <div className="mt-3 inline-block rounded-full bg-neon-gold px-4 py-1.5 text-xs font-black text-black" data-testid="staff-scan-reward">
                    REWARD: {result.reward_unlocked}
                  </div>
                )}
                {!result.already_checked_in && (
                  <div className="mt-2 text-[11px] text-zinc-500">
                    +{result.points_awarded} pts · {result.entry_type}{result.prepaid ? " (prepaid)" : ""}
                  </div>
                )}
                <button data-testid="staff-scan-next-btn" onClick={() => setResult(null)}
                  className="btn-neon mt-4 w-full !py-2.5 text-sm">Scan Next Guest</button>
              </motion.div>
            )}
          </AnimatePresence>

          {!result && (
            <div className="mt-5 space-y-4">
              {showCamera ? (
                <QrScanner onScan={(decoded) => doScan(decoded)} onClose={() => setShowCamera(false)} />
              ) : (
                <button data-testid="staff-camera-btn" onClick={() => setShowCamera(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-neon-violet/40 bg-neon-violet/10 py-4 text-sm font-black text-neon-violet">
                  <Camera size={20} /> Open Camera Scanner
                </button>
              )}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-500">
                  <QrCode size={14} /> Manual code entry
                </div>
                <input ref={tokenRef} data-testid="staff-token-input" value={token} autoFocus
                  onChange={(e) => setToken(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan()}
                  placeholder="Guest's NITEPASS code"
                  className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 font-mono text-sm outline-none focus:border-neon-gold" />
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <select data-testid="staff-entry-select" value={entryPoint} onChange={(e) => setEntryPoint(e.target.value)}
                    className="rounded-xl border border-white/10 bg-black px-3 py-2.5 text-xs font-bold outline-none">
                    {ENTRY_POINTS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input data-testid="staff-amount-input" type="number" min="0" value={amount}
                    onChange={(e) => setAmount(e.target.value)} placeholder="$ paid"
                    className="rounded-xl border border-white/10 bg-black px-3 py-2.5 text-xs font-bold outline-none focus:border-neon-gold" />
                </div>
                <button data-testid="staff-scan-submit" disabled={busy || !token.trim()} onClick={() => doScan()}
                  className="btn-neon mt-3 w-full disabled:opacity-40">
                  {busy ? "Checking..." : "Check In Guest"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
